import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useOrganization } from "../organization/OrganizationProvider";

type CustomerOption = {
  id: string;
  name: string;
  document_type: string | null;
  document_number: string | null;
  status: "active" | "inactive" | "archived";
};

type QuotationStatus =
  | "draft"
  | "sent"
  | "viewed"
  | "approved"
  | "rejected"
  | "expired"
  | "cancelled";

type QuotationRow = {
  id: string;
  quotation_number: string;
  status: QuotationStatus;
  issue_date: string;
  valid_until: string | null;
  currency_code: string;
  subtotal: number | string;
  discount_total: number | string;
  tax_total: number | string;
  total: number | string;
  customer_id: string | null;
  customer_name: string | null;
  updated_at: string;
};

type LineItem = {
  description: string;
  quantity: string;
  unitPrice: string;
  discountAmount: string;
  taxRate: string;
};

const EMPTY_LINE: LineItem = {
  description: "",
  quantity: "1",
  unitPrice: "0",
  discountAmount: "0",
  taxRate: "18",
};

function limaDate(offsetDays = 0) {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Lima",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);

  const year = Number(parts.find((part) => part.type === "year")?.value ?? "0");
  const month = Number(parts.find((part) => part.type === "month")?.value ?? "1");
  const day = Number(parts.find((part) => part.type === "day")?.value ?? "1");

  const date = new Date(Date.UTC(year, month - 1, day + offsetDays));
  return date.toISOString().slice(0, 10);
}

function money(value: number | string, currency = "PEN") {
  return new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency,
  }).format(Number(value || 0));
}

const STATUS_LABELS: Record<QuotationStatus, string> = {
  draft: "Borrador",
  sent: "Enviada",
  viewed: "Vista",
  approved: "Aprobada",
  rejected: "Rechazada",
  expired: "Vencida",
  cancelled: "Cancelada",
};

function statusActions(status: QuotationStatus) {
  switch (status) {
    case "draft":
      return [
        { value: "sent" as const, label: "Marcar enviada" },
        { value: "cancelled" as const, label: "Cancelar", danger: true },
      ];
    case "sent":
      return [
        { value: "viewed" as const, label: "Marcar vista" },
        { value: "approved" as const, label: "Aprobar" },
        { value: "rejected" as const, label: "Rechazar", danger: true },
        { value: "cancelled" as const, label: "Cancelar", danger: true },
      ];
    case "viewed":
      return [
        { value: "approved" as const, label: "Aprobar" },
        { value: "rejected" as const, label: "Rechazar", danger: true },
        { value: "cancelled" as const, label: "Cancelar", danger: true },
      ];
    default:
      return [];
  }
}

export function QuotationsPage() {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.organizationId ?? null;

  const [quotations, setQuotations] = useState<QuotationRow[]>([]);
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [validUntil, setValidUntil] = useState(limaDate(15));
  const [notes, setNotes] = useState("");
  const [terms, setTerms] = useState("Validez de la cotización según fecha indicada.");
  const [items, setItems] = useState<LineItem[]>([{ ...EMPTY_LINE }]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [changingStatusId, setChangingStatusId] = useState<string | null>(null);

  const loadQuotations = useCallback(
    async (query: string) => {
      if (!organizationId) {
        setQuotations([]);
        setCustomers([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError(null);

      const [quotationResult, customerResult] = await Promise.all([
        supabase.rpc("list_organization_quotations", {
          p_organization_id: organizationId,
          p_search: query.trim() || null,
        }),
        supabase.rpc("list_organization_customers", {
          p_organization_id: organizationId,
          p_search: null,
        }),
      ]);

      if (quotationResult.error) {
        setLoadError(quotationResult.error.message);
        setQuotations([]);
      } else {
        setQuotations((quotationResult.data ?? []) as QuotationRow[]);
      }

      if (customerResult.error) {
        setLoadError((current) =>
          current
            ? `${current} · ${customerResult.error?.message ?? ""}`
            : customerResult.error?.message ?? "No se pudieron cargar los clientes.",
        );
        setCustomers([]);
      } else {
        setCustomers(
          ((customerResult.data ?? []) as CustomerOption[]).filter(
            (customer) => customer.status === "active",
          ),
        );
      }

      setLoading(false);
    },
    [organizationId],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadQuotations(search);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [loadQuotations, search]);

  useEffect(() => {
    setSearch("");
    setFormOpen(false);
    setCustomerId("");
    setValidUntil(limaDate(15));
    setNotes("");
    setItems([{ ...EMPTY_LINE }]);
    setFormError(null);
    setFormSuccess(null);
    setStatusError(null);
    setChangingStatusId(null);
  }, [organizationId]);

  const estimatedTotal = useMemo(
    () =>
      items.reduce((total, item) => {
        const quantity = Number(item.quantity || 0);
        const price = Number(item.unitPrice || 0);
        const discount = Number(item.discountAmount || 0);
        const taxRate = Number(item.taxRate || 0);
        const net = Math.max(quantity * price - discount, 0);
        return total + net + net * taxRate / 100;
      }, 0),
    [items],
  );

  function updateLine(index: number, patch: Partial<LineItem>) {
    setItems((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item,
      ),
    );
  }

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!organizationId) return;
    if (!customerId) {
      setFormError("Selecciona un cliente.");
      return;
    }

    const normalizedItems = items.map((item) => ({
      description: item.description.trim(),
      quantity: Number(item.quantity),
      unit_price: Number(item.unitPrice),
      discount_amount: Number(item.discountAmount),
      tax_rate: Number(item.taxRate),
    }));

    if (
      normalizedItems.some(
        (item) =>
          !item.description ||
          !Number.isFinite(item.quantity) ||
          item.quantity <= 0 ||
          !Number.isFinite(item.unit_price) ||
          item.unit_price < 0,
      )
    ) {
      setFormError("Revisa la descripción, cantidad y precio de cada ítem.");
      return;
    }

    setSaving(true);
    setFormError(null);
    setFormSuccess(null);

    const { error } = await supabase.rpc("create_organization_quotation", {
      p_organization_id: organizationId,
      p_customer_id: customerId,
      p_valid_until: validUntil || null,
      p_currency_code: "PEN",
      p_notes: notes.trim() || null,
      p_terms: terms.trim() || null,
      p_items: normalizedItems,
    });

    setSaving(false);

    if (error) {
      setFormError(error.message);
      return;
    }

    setCustomerId("");
    setValidUntil(limaDate(15));
    setNotes("");
    setItems([{ ...EMPTY_LINE }]);
    setFormSuccess("Cotización creada correctamente.");
    await loadQuotations("");
  }

  async function changeStatus(
    quotationId: string,
    nextStatus: QuotationStatus,
  ) {
    setChangingStatusId(quotationId);
    setStatusError(null);

    const { error } = await supabase.rpc("transition_quotation_status", {
      p_quotation_id: quotationId,
      p_new_status: nextStatus,
    });

    setChangingStatusId(null);

    if (error) {
      setStatusError(error.message);
      return;
    }

    await loadQuotations(search);
  }

  if (!selectedOrganization) {
    return (
      <div className="page">
        <section className="empty-state">
          <strong>Selecciona una empresa.</strong>
          <p>Necesitas una empresa activa para trabajar con cotizaciones.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="page-header page-header-actions">
        <div>
          <span className="eyebrow">Cotizaciones</span>
          <h1>{selectedOrganization.name}</h1>
          <p>Propuestas comerciales con cálculo automático de impuestos.</p>
        </div>

        <button
          className="primary-button"
          type="button"
          onClick={() => {
            setFormOpen((value) => !value);
            setFormError(null);
            setFormSuccess(null);
          }}
        >
          {formOpen ? "Cerrar" : "Nueva cotización"}
        </button>
      </header>

      {formOpen ? (
        <section className="panel-card">
          <div className="section-heading">
            <div>
              <span className="eyebrow">Nueva</span>
              <h2>Cotización</h2>
            </div>
            <strong className="quotation-estimate">
              {money(estimatedTotal)}
            </strong>
          </div>

          <form className="quotation-form" onSubmit={handleCreate}>
            <div className="quotation-header-grid">
              <label>
                Cliente
                <select
                  required
                  value={customerId}
                  onChange={(event) => setCustomerId(event.target.value)}
                >
                  <option value="">Seleccionar cliente</option>
                  {customers.map((customer) => (
                    <option value={customer.id} key={customer.id}>
                      {customer.name}
                      {customer.document_number
                        ? ` · ${customer.document_number}`
                        : ""}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Válida hasta
                <input
                  type="date"
                  min={limaDate()}
                  value={validUntil}
                  onChange={(event) => setValidUntil(event.target.value)}
                />
              </label>

              <label className="field-span-2">
                Notas
                <input
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder="Observaciones para el cliente"
                />
              </label>

              <label className="field-span-2">
                Términos
                <input
                  value={terms}
                  onChange={(event) => setTerms(event.target.value)}
                />
              </label>
            </div>

            <div className="quotation-items">
              <div className="quotation-items-heading">
                <strong>Ítems</strong>
                <button
                  className="ghost-button"
                  type="button"
                  onClick={() =>
                    setItems((current) => [...current, { ...EMPTY_LINE }])
                  }
                >
                  Agregar ítem
                </button>
              </div>

              {items.map((item, index) => (
                <div className="quotation-line" key={index}>
                  <label className="quotation-description">
                    Descripción
                    <input
                      required
                      value={item.description}
                      onChange={(event) =>
                        updateLine(index, { description: event.target.value })
                      }
                    />
                  </label>

                  <label>
                    Cantidad
                    <input
                      type="number"
                      min="0.001"
                      step="0.001"
                      value={item.quantity}
                      onChange={(event) =>
                        updateLine(index, { quantity: event.target.value })
                      }
                    />
                  </label>

                  <label>
                    Precio base
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unitPrice}
                      onChange={(event) =>
                        updateLine(index, { unitPrice: event.target.value })
                      }
                    />
                  </label>

                  <label>
                    Descuento
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.discountAmount}
                      onChange={(event) =>
                        updateLine(index, { discountAmount: event.target.value })
                      }
                    />
                  </label>

                  <label>
                    IGV %
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={item.taxRate}
                      onChange={(event) =>
                        updateLine(index, { taxRate: event.target.value })
                      }
                    />
                  </label>

                  <button
                    className="danger-ghost-button"
                    type="button"
                    disabled={items.length === 1}
                    onClick={() =>
                      setItems((current) =>
                        current.filter((_line, itemIndex) => itemIndex !== index),
                      )
                    }
                  >
                    Quitar
                  </button>
                </div>
              ))}
            </div>

            {formError ? <div className="form-error">{formError}</div> : null}
            {formSuccess ? <div className="form-success">{formSuccess}</div> : null}

            <div className="form-actions">
              <button className="primary-button" type="submit" disabled={saving}>
                {saving ? "Guardando…" : "Crear cotización"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      {statusError ? (
        <div className="inline-alert" role="alert">
          No se pudo cambiar el estado: {statusError}
        </div>
      ) : null}

      <section className="toolbar-card">
        <div>
          <strong>{loading ? "…" : quotations.length}</strong>
          <span> cotizaciones</span>
        </div>
        <input
          type="search"
          placeholder="Buscar por número o cliente"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </section>

      {loadError ? (
        <div className="inline-alert" role="alert">
          {loadError}
        </div>
      ) : null}

      {!loading && quotations.length === 0 && !loadError ? (
        <section className="empty-state">
          <strong>No hay cotizaciones para mostrar.</strong>
          <p>Crea la primera cotización o cambia la búsqueda.</p>
        </section>
      ) : null}

      <section className="quotation-grid" aria-label="Listado de cotizaciones">
        {quotations.map((quotation) => (
          <article className="quotation-card" key={quotation.id}>
            <div className="quotation-card-heading">
              <div>
                <span className={`status-badge status-badge-${quotation.status}`}>
                  {STATUS_LABELS[quotation.status]}
                </span>
                <h2>{quotation.quotation_number}</h2>
                <p>{quotation.customer_name ?? "Cliente no disponible"}</p>
              </div>
              <strong>{money(quotation.total, quotation.currency_code)}</strong>
            </div>

            <dl className="customer-meta">
              <div>
                <dt>Emisión</dt>
                <dd>{quotation.issue_date}</dd>
              </div>
              <div>
                <dt>Validez</dt>
                <dd>{quotation.valid_until ?? "—"}</dd>
              </div>
              <div>
                <dt>Base</dt>
                <dd>{money(quotation.subtotal, quotation.currency_code)}</dd>
              </div>
              <div>
                <dt>IGV</dt>
                <dd>{money(quotation.tax_total, quotation.currency_code)}</dd>
              </div>
            </dl>

            <div className="quotation-actions">
              <Link
                className="compact-button"
                to={`/cotizaciones/${quotation.id}`}
              >
                Ver detalle
              </Link>

              {statusActions(quotation.status).map((action) => (
                <button
                  key={action.value}
                  type="button"
                  className={
                    action.danger
                      ? "compact-button compact-button-danger"
                      : "compact-button"
                  }
                  disabled={changingStatusId === quotation.id}
                  onClick={() => void changeStatus(quotation.id, action.value)}
                >
                  {changingStatusId === quotation.id
                    ? "Actualizando…"
                    : action.label}
                </button>
              ))}
            </div>

            {quotation.status === "approved" ? (
              <div className="quotation-terminal-note">
                Lista para convertir en orden de servicio.
              </div>
            ) : null}
          </article>
        ))}
      </section>
    </div>
  );
}
